import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea,
  Button, Container, Paper, Alert, CircularProgress, Fade, Zoom
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { selectRole } from '../../services/userService';

// Enhanced styled components matching Login.js
const GlassContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: 'transparent',
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(25px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '24px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)',
  padding: theme.spacing(5),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '50%',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)',
    borderRadius: '24px 24px 0 0',
    pointerEvents: 'none',
  },

  '&:hover': {
    background: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.3)',
    transform: 'translateY(-4px)',

    '&::before': {
      opacity: 0.7,
    }
  }
}));

const GlassTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    borderRadius: '2px',
  }
}));

const RoleCard = styled(Card)(({ theme, selected }) => ({
  background: selected
    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
    : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(12px)',
  borderRadius: '20px',
  border: selected
    ? '2px solid #1976d2'
    : '2px solid rgba(255, 255, 255, 0.3)',
  boxShadow: selected
    ? '0 12px 40px rgba(25, 118, 210, 0.3)'
    : '0 8px 25px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',

  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: selected
      ? '0 20px 60px rgba(25, 118, 210, 0.4)'
      : '0 15px 45px rgba(0, 0, 0, 0.2)',
  }
}));

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const EnhancedButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  minHeight: '56px',
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-200px',
    width: '200px',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
    animation: `${shimmerAnimation} 2s infinite`,
  },

  '&:hover': {
    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    boxShadow: '0 12px 40px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-3px) scale(1.02)',

    '&::before': {
      animation: `${shimmerAnimation} 1s infinite`,
    }
  },

  '&:active': {
    transform: 'translateY(-1px) scale(1.01)',
    animation: `${pulseAnimation} 0.3s ease`,
  },

  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
    boxShadow: 'none',
    transform: 'none',

    '&::before': {
      display: 'none',
    }
  }
}));

const RoleSelection = ({ onRoleSelected }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const roles = [
    {
      id: 'brand',
      title: 'Brand',
      description: 'I want to create campaigns and collaborate with influencers',
      icon: <BusinessIcon sx={{ fontSize: 80 }} />
    },
    {
      id: 'influencer',
      title: 'Influencer',
      description: 'I want to apply to campaigns and create content',
      icon: <PersonIcon sx={{ fontSize: 80 }} />
    }
  ];

  const handleRoleClick = (roleId) => {
    setSelectedRole(roleId);
    setError(null);
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      setError('Please select a role to continue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await selectRole(selectedRole);

      if (result.success) {
        // Call the onRoleSelected callback to trigger redirect
        if (onRoleSelected) {
          onRoleSelected(selectedRole);
        }
      } else {
        setError(result.error || 'Failed to assign role');
      }
    } catch (err) {
      console.error('Error selecting role:', err);
      setError(err.message || 'Failed to assign role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlassContainer maxWidth="md">
      <Fade in timeout={800}>
        <Box>
          <Zoom in timeout={600}>
            <GlassPaper elevation={0}>
              <GlassTypography component="h1" variant="h4">
                Welcome to InfHire
              </GlassTypography>

              <Typography
                variant="h6"
                color="text.secondary"
                align="center"
                sx={{ mb: 3, fontWeight: 500 }}
              >
                Choose your account type to get started
              </Typography>

              <Fade in timeout={600}>
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    borderRadius: '16px',
                    background: 'rgba(33, 150, 243, 0.1)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(33, 150, 243, 0.3)',
                  }}
                >
                  We don't see an existing account, but no worries! Let's get you set up. Choose your account type below to continue.
                </Alert>
              </Fade>

              {error && (
                <Fade in timeout={400}>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '16px' }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
                {roles.map((role, index) => (
                  <Fade
                    key={role.id}
                    in
                    timeout={800}
                    style={{ transitionDelay: `${(index + 1) * 200}ms` }}
                  >
                    <RoleCard selected={selectedRole === role.id}>
                      <CardActionArea
                        onClick={() => handleRoleClick(role.id)}
                        sx={{ height: '100%' }}
                      >
                        <CardContent sx={{
                          textAlign: 'center',
                          py: 4,
                          color: selectedRole === role.id ? 'white' : 'inherit'
                        }}>
                          <Box sx={{
                            mb: 2,
                            color: selectedRole === role.id ? 'white' : 'primary.main'
                          }}>
                            {role.icon}
                          </Box>
                          <Typography
                            variant="h5"
                            component="h2"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {role.title}
                          </Typography>
                          <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            {role.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </RoleCard>
                  </Fade>
                ))}
              </Box>

              <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
                <Box>
                  <EnhancedButton
                    fullWidth
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!selectedRole || loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      'Continue'
                    )}
                  </EnhancedButton>
                </Box>
              </Fade>
            </GlassPaper>
          </Zoom>
        </Box>
      </Fade>
    </GlassContainer>
  );
};

export default RoleSelection;
